/**
 * Filtro por áreas dos "Últimos focos" (Zustand): até 3 áreas (municípios,
 * estados, sub-regiões, TIs, UCs). A lista e o contador mostram os focos que
 * caem em QUALQUER uma delas (união). O mapa não é filtrado.
 *
 * Registra o codec de URL `a` no import:
 *   a=m1500602*tKayapó*ePA → itens separados por "*", cada um com o código
 *   de 1 letra do tipo (`AREA_KINDS`) seguido da chave da área.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import { areaId, areaKind, areaKindByCode, getLoadedAreaIndex, loadAreaIndex, type AreaRef } from './areaSearch';

export const MAX_AREAS = 3;

export interface AreaFilterState {
  areas: AreaRef[];
  /** Acrescenta (ignora repetida ou além do limite). */
  addArea: (area: AreaRef) => void;
  removeArea: (id: string) => void;
  clearAreas: () => void;
  /** Completa nomes das áreas vindas do link, quando o índice carrega. */
  fillNames: () => void;
}

export const useAreaFilterStore = create<AreaFilterState>()((set, get) => ({
  areas: [],
  addArea: (area) => {
    const { areas } = get();
    if (areas.length >= MAX_AREAS || areas.some((a) => areaId(a) === areaId(area))) return;
    set({ areas: [...areas, { layer: area.layer, key: area.key, name: area.name, detail: area.detail }] });
  },
  removeArea: (id) => set((s) => ({ areas: s.areas.filter((a) => areaId(a) !== id) })),
  clearAreas: () => set({ areas: [] }),
  fillNames: () => {
    const index = getLoadedAreaIndex();
    if (!index || get().areas.every((a) => a.name !== null)) return;
    set((s) => ({
      areas: s.areas.map((a) => {
        if (a.name !== null) return a;
        const e = index.byId.get(areaId(a));
        return e ? { ...a, name: e.name, detail: e.detail } : a;
      }),
    }));
  },
}));

// ---------------------------------------------------------------------------
// Link compartilhável — chave `a`
// ---------------------------------------------------------------------------

export function encodeAreasUrl(areas: readonly Pick<AreaRef, 'layer' | 'key'>[]): string | null {
  const items = areas.filter((a) => !a.key.includes('*')).map((a) => `${areaKind(a.layer).code}${a.key}`);
  return items.length ? items.join('*') : null;
}

export function decodeAreasUrl(value: string): AreaRef[] {
  const out: AreaRef[] = [];
  const seen = new Set<string>();
  for (const item of String(value).split('*')) {
    const kind = areaKindByCode(item.charAt(0));
    const key = item.slice(1).trim();
    if (!kind || !key || key.length > 120) continue;
    const ref: AreaRef = { layer: kind.id, key, name: null, detail: '' };
    if (seen.has(areaId(ref))) continue;
    seen.add(areaId(ref));
    out.push(ref);
    if (out.length >= MAX_AREAS) break;
  }
  return out;
}

registerUrlCodec({
  key: 'a',
  encode: () => encodeAreasUrl(useAreaFilterStore.getState().areas),
  decode: (value) => {
    const areas = decodeAreasUrl(value);
    if (!areas.length) return;
    useAreaFilterStore.setState({ areas });
    // Nomes para os chips (o índice é pequeno); sem ele, os chips mostram a chave.
    void loadAreaIndex()
      .then(() => useAreaFilterStore.getState().fillNames())
      .catch(() => {});
  },
  subscribe: (onChange) =>
    useAreaFilterStore.subscribe((s, prev) => {
      if (s.areas !== prev.areas) onChange();
    }),
});
